import { jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { MultiSelect } from "../MultiSelect.js";
import { useActions } from "../hooks/useActions.js";
import { useFilterOptions } from "../hooks/useFilterOptions.js";
import { useOptions } from "../hooks/useOptions.js";
import { defaultIsOptionEqualToValue } from "../utils/defaultIsOptionEqualToValue.js";
import { stateContext } from "./stateContext.js";
import {
  DEFAULT_MAX_VISIBLE_SELECTED_ITEMS,
  DEFAULT_MAX_VISIBLE_SELECTED_ITEMS_ON_FOCUS,
  DEFAULT_VIRTUAL_LIST_BUFFER,
  defaultGetItemHeights,
  defaultGetLimitTagsText,
  defaultGetOptionLabel
} from "./utils.js";
function defaultGetOptionDisabled() {
  return false;
}
function MultiSelectStateProvider(props) {
  const {
    value,
    items,
    testId,
    onChange,
    matchFilter,
    autocompleteProps,
    getOptionDisabled = defaultGetOptionDisabled
  } = props;
  const searchState = useState("");
  const [searchValue] = searchState;
  const getOptionLabel = props.getLabel ?? defaultGetOptionLabel;
  const getItemHeights = props.listMode === "virtual" ? props?.getItemHeights ?? defaultGetItemHeights : defaultGetItemHeights;
  const getLimitTagsText = props.optionMode === "normal" ? props?.getLimitTagsText ?? defaultGetLimitTagsText : defaultGetLimitTagsText;
  const buffer = props.listMode === "virtual" ? props?.buffer ?? DEFAULT_VIRTUAL_LIST_BUFFER : DEFAULT_VIRTUAL_LIST_BUFFER;
  const maxSelectedItemsToShow = props.inputMode === "normal" ? props.maxSelectedItemsToShow || DEFAULT_MAX_VISIBLE_SELECTED_ITEMS : DEFAULT_MAX_VISIBLE_SELECTED_ITEMS;
  const maxSelectedItemsToShowOnFocus = props.inputMode === "normal" ? props.maxSelectedItemsToShowOnFocus || DEFAULT_MAX_VISIBLE_SELECTED_ITEMS_ON_FOCUS : DEFAULT_MAX_VISIBLE_SELECTED_ITEMS_ON_FOCUS;
  const isOptionEqualToValue = autocompleteProps?.isOptionEqualToValue ?? defaultIsOptionEqualToValue;
  const { visibleOptions, selectedOptions, selectableOptions, setVisibleOptions } = useOptions(
    items,
    value,
    getOptionDisabled,
    isOptionEqualToValue
  );
  const filterOptions = useFilterOptions(matchFilter);
  const actions = useActions(value, selectableOptions, selectedOptions, isOptionEqualToValue, onChange);
  const multiSelectState = useMemo(() => {
    return {
      props,
      state: { visibleOptions, selectedOptions, selectableOptions },
      buffer,
      actions,
      searchState,
      filterOptions,
      getOptionLabel,
      getItemHeights,
      getLimitTagsText,
      getOptionDisabled,
      isOptionEqualToValue,
      maxSelectedItemsToShow,
      maxSelectedItemsToShowOnFocus
    };
  }, [
    props,
    buffer,
    actions,
    searchState,
    filterOptions,
    getOptionLabel,
    visibleOptions,
    getItemHeights,
    selectedOptions,
    getLimitTagsText,
    selectableOptions,
    getOptionDisabled,
    isOptionEqualToValue,
    maxSelectedItemsToShow,
    maxSelectedItemsToShowOnFocus
  ]);
  useEffect(() => {
    if (searchValue === "") {
      setVisibleOptions(items);
      return;
    }
    setVisibleOptions(
      filterOptions(items, {
        getOptionLabel,
        inputValue: searchValue
      })
    );
  }, [items, searchValue, filterOptions, getOptionLabel, setVisibleOptions]);
  return /* @__PURE__ */ jsx(stateContext.Provider, { value: multiSelectState, children: /* @__PURE__ */ jsx(MultiSelect, { testId, multiSelectState, setVisibleOptions }) });
}
export {
  MultiSelectStateProvider
};
//# sourceMappingURL=MultiSelectStateProvider.js.map
